import { BLOB_CONTENT_ENDPOINT, JOURNAL_THUMBNAIL_ENDPOINT } from "api/endpoints";
import { ButtonV2 } from "components/Button";
import { FormField, FormFieldNative } from "components/Form";
import { Input, inputClassNames } from "components/Input";
import { NamedModalWindow } from "components/ModalWindow";
import { Textarea } from "components/Textarea";
import { quartileOptions, vakOptions, whitelistOptions } from "data/journal-indexes";
import { useAPIFetch } from "hooks/fetch";
import { Journal } from "models/journal";
import { FormEventHandler, useRef, useState } from 'react'
import InputMask from 'react-input-mask'
import ReactSelect from "react-select";
import { twMerge } from "tailwind-merge";
import { topicList } from "data/journal-topics";
import { indexOptions } from "data/journal-indexes";
import { Select } from "antd";

export function JournalEditModal({ journal, onSubmit, title, submitText, open, close }: { journal?: Journal; title?: string; submitText?: string; close: () => void; open?: boolean; onSubmit: (v: Partial<Journal & { thumbnail?: File }>) => void; }) {

  // const [errors, setErrors] = useState<Record<string, string>>({});

  const [issn, setIssn] = useState(journal?.issn)
  const [issnOnline, setIssnOnline] = useState(journal?.issn_online)
  const thumbnailUploadRef = useRef<HTMLInputElement>(null)
  const [thumbnailUrl, setThumbnailUrl] = useState(journal?.thumbnail_blob_id ? BLOB_CONTENT_ENDPOINT(journal.thumbnail_blob_id) : journal?.thumbnail_url)
  const [thumbnail, setThumbnail] = useState<File>()

  const [indexes, setIndexes] = useState<(Partial<{ kind: string; link?: string }> & { id: string })[]>(Object.entries(journal?.indexes_v2 ?? []).map(v => ({ kind: v[0], link: v[1] === '' ? undefined : v[1], id: Math.random().toString() })))
  const [topics, setTopics] = useState<string[] | undefined>(journal?.topics)  // TODO: implement on backend
  const apiFetch = useAPIFetch()

  const handleClose = () => {
    setIndexes([])
    setTopics(undefined)
    setThumbnail(undefined)
    setIssn(undefined)
    setIssnOnline(undefined)
    close()
  }

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);

    const { name, name_international, homepage_url, guidelines_url, contacts_url } = Object.fromEntries(form) as Partial<{
      name: string
      name_international: string
      guidelines_url: string
      homepage_url: string
      contacts_url: string
    }>

    onSubmit({
      name,
      name_international,
      url: homepage_url,
      guidelines_url,
      indexes_v2: Object.fromEntries(indexes.filter(v => v.kind !== undefined).map(v => [v.kind!, v.link ?? ""])),
      issn,
      issn_online: issnOnline,
      thumbnail,
      topics,
      contacts_url,
    })
  };


  return <NamedModalWindow name={title ?? "Редактирование журнала"} close={handleClose} closable open={open ?? false}>
    <form className="flex flex-col gap-1 py-1 w-60 max-3xl:w-50 rounded-md" onSubmit={handleSubmit}>
      <div className="flex flex-row gap-2 flex-1">
        {/* <div className="flex flex-col gap-1 w-full"> */}
        <div className="flex flex-col gap-1 flex-1">
          <div className="flex flex-row gap-1">
            <FormField name="Обложка" className="flex flex-col" required>
              <label className="p-0.5 border-grey border-2 border-dashed rounded-lg h-fit w-fit relative group">
                {thumbnailUrl ? <>
                  <div className="w-6 h-7 group-hover:brightness-50 bg-white rounded-lg" style={{ backgroundImage: `url('${thumbnailUrl}')`, backgroundPosition: 'center center', backgroundRepeat: 'no-repeat', backgroundSize: 'contain' }}></div>
                  <button className="absolute group-hover:flex hidden h-full w-full items-center justify-center left-0 top-0" onClick={e => {
                    e.preventDefault()
                    thumbnailUploadRef.current?.click()
                  }}>
                    <img src="/images/blank-image-white.svg" className="w-2 h-2" />
                  </button>
                  <input hidden type="file" accept=".png,.svg,.jpg,.jpeg" ref={thumbnailUploadRef} onChange={async e => {
                    if (!e.target.files)
                      return
                    setThumbnail(e.target.files![0]!)
                    setThumbnailUrl(URL.createObjectURL((e.target.files![0]!)))

                  }} name="thumbnail" />

                </> : <>
                  <button className="flex-shrink-0 w-6 h-7 flex items-center justify-center" onClick={e => {
                    e.preventDefault()
                    thumbnailUploadRef.current?.click()
                  }}>
                    <img src="/images/blank-image.svg" className="w-2 h-2" />
                  </button>
                  <input hidden type="file" name="thumbnail" accept=".png,.svg,.jpg,.jpeg" ref={thumbnailUploadRef} onChange={async e => {
                    setThumbnail(e.target.files![0]!)
                    setThumbnailUrl(URL.createObjectURL((e.target.files![0]!)))

                  }} />
                </>}
              </label>
            </FormField>

            <div className="flex flex-col gap-1 w-full">
              <FormFieldNative name="Название" required>
                <Input name="name" className="w-full" defaultValue={journal?.name} required placeholder="Название журнала" />
              </FormFieldNative>
              <FormFieldNative name="Название (на английском)" required>
                <Input name="name_international" className="w-full" defaultValue={journal?.name_international} required placeholder="Название журнала на английском языке" />
              </FormFieldNative>
            </div>

          </div>
        </div>
        <div className="flex flex-col gap-1 flex-1">
          <FormFieldNative name="Главная страница" className="w-full" required>
            <Input defaultValue={journal?.url} placeholder="https://example.com" className="w-full" type="url" name="homepage_url" required />
          </FormFieldNative>
          <FormFieldNative name="Ссылка на правила" className="w-full" required>
            <Input defaultValue={journal?.guidelines_url} className="w-full" placeholder="https://example.com" type="url" name="guidelines_url" required />
          </FormFieldNative>
          <FormFieldNative name="Ссылка на контакты редакции" className="w-full" required>
            <Input defaultValue={journal?.contacts_url} className="w-full" placeholder="https://example.com" type="url" name="contacts_url" required />
          </FormFieldNative>
          <div className="flex flex-row gap-1">
            <FormFieldNative name="ISSN (print)" className="w-7" required>
              <InputMask mask="9999-9999" placeholder="0000-0000" pattern="[0-9a-zA-Z]{4}-[0-9a-zA-Z]{4}" required className={twMerge(inputClassNames, "flex-1 w-full")} defaultValue={issn} onChange={e => {
                setIssn(e.target.value)
              }} />
            </FormFieldNative>
            <FormFieldNative name="ISSN (online)" className="w-7" required>
              <InputMask mask="****-****" placeholder="xxxx-xxxx" pattern="[0-9a-zA-Z]{4}-[0-9a-zA-Z]{4}" required className={twMerge(inputClassNames, "flex-1 w-full")} defaultValue={issnOnline} onChange={e => {
                setIssnOnline(e.target.value)
              }} />
            </FormFieldNative>

          </div>
          {!journal && <FormField name="Тематика" required>
            <ReactSelect
              // tooltip="Стиль"
              isMulti
              options={topicList.map(v => ({ label: v[0], value: v[0], bold: v[1] }))}
              classNamePrefix="react-menu"
              value={topics ? topics.map(v => ({ label: v, value: v })) : undefined}
              onChange={v => {
                console.log('tea', v)
                setTopics((v ?? []).map(v => v.value))
              }}
              menuPosition="fixed"
              placeholder="Выберите тематику вашего журнала"
              styles={{
                placeholder: (base, state) => ({
                  ...base,
                  color: 'var(--color-grey)',
                }),
                // singleValue: (styles, { data }) => ({
                //   ...styles,
                //   fontWeight: (data.bold ? 'bold' : undefined),
                // }),
                option: (styles, { data }) => ({
                  ...styles,
                  fontWeight: (data.bold ? '600' : undefined), // semibold; taken from https://tailwindcss.com/docs/font-weight
                })

              }}
              name="topic"
            />
          </FormField>}

          <FormField name="Индексация">
            {/* {JSON.stringify(indexes)} */}
            <div className="w-full flex flex-col gap-1">
              {/* {JSON.stringify(indexes)} */}
              {indexes.map(index => <div className="flex flex-row gap-1 w-full items-center" key={index.id}>
                <ReactSelect
                  placeholder="Индекс"
                  options={indexOptions.filter(v => indexes.findIndex(v2 => v.value === v2.kind) < 0)}
                  defaultValue={indexOptions.find(v => v.value === index.kind)}

                  menuPosition="fixed"
                  onChange={v => {
                    const tmp = [...indexes]
                    const idx = tmp.findIndex(v2 => v2.id === index.id)
                    tmp[idx].kind = v.value
                    setIndexes(tmp)
                  }}
                  className="w-20 h-min"
                />
                <Input placeholder="Ссылка (при наличии)" className="w-full" defaultValue={index.link} setValue={v => {
                  const tmp = [...indexes]
                  const idx = tmp.findIndex(v2 => v2.id === index.id)
                  tmp[idx].link = v
                  setIndexes(tmp)
                }} type="url" />
                <button onClick={e => {
                  e.preventDefault()
                  const tmp = [...indexes]
                  const idx = tmp.findIndex(v2 => v2.id === index.id)
                  tmp.splice(idx, 1)
                  setIndexes(tmp)

                }}><img src="/images/bin.svg" className="w-1 h-1" /></button>
              </div>)}

              <button className="w-full border border-grey border-dashed flex justify-center items-center rounded-xl p-0.5" onClick={e => {
                e.preventDefault()
                setIndexes([...indexes, { id: Math.random().toString() }])
              }}>
                <img src="/images/add-grey.svg" className="w-1 h-1" />
              </button>
            </div>
          </FormField>
          {/* <div className="flex flex-row gap-1"> */}
          {/*   <FormField name="Индексы - SJR" className="w-full"> */}
          {/*     <Input className="w-full" placeholder="Введите ссылку или оставьте поле пустым" type="url" /> */}
          {/*   </FormField> */}
          {/*   <FormField name="Индексы - Mendeley"  className="w-full"> */}
          {/*     <Input className="w-full" placeholder="Введите ссылку или оставьте поле пустым" type="url" /> */}
          {/*   </FormField> */}
          {/* </div> */}
          {/* <div className="flex flex-row gap-1"> */}
          {/*   <FormField name="Индексы - eLibrary"  className="w-full"> */}
          {/*     <Input className="w-full" placeholder="Введите ссылку или оставьте поле пустым" type="url" /> */}
          {/*   </FormField> */}
          {/*   <FormField name="Индексы - Google Scholar"  className="w-full"> */}
          {/*     <Input className="w-full" placeholder="Введите ссылку или оставьте поле пустым" type="url" /> */}
          {/*   </FormField> */}
          {/* </div> */}
        </div>

      </div>

      <div className="flex flex-row justify-end gap-1 w-full">
        <ButtonV2 onClick={handleClose} type="reset" colorscheme="light">Отмена</ButtonV2>
        <ButtonV2 type="submit" disabled={(!journal && !topics) || !(thumbnail || thumbnailUrl)}>{submitText ?? 'Сохранить'}</ButtonV2>
      </div>
    </form>
  </NamedModalWindow>
}
